import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PasswordSetupStep = ({ setupData, setSetupData, onNext, onBack }) => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password) => {
    const requirements = {
      length: password?.length >= 8,
      uppercase: /[A-Z]/?.test(password),
      lowercase: /[a-z]/?.test(password),
      number: /[0-9]/?.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/?.test(password)
    };
    return requirements;
  };

  const getPasswordStrength = (password) => {
    const requirements = validatePassword(password);
    const metCount = Object.values(requirements)?.filter(Boolean)?.length;
    
    if (metCount <= 2) return { label: 'Weak', color: 'text-error', bgColor: 'bg-error' };
    if (metCount === 3) return { label: 'Fair', color: 'text-warning', bgColor: 'bg-warning' };
    if (metCount === 4) return { label: 'Good', color: 'text-success', bgColor: 'bg-success' };
    return { label: 'Strong', color: 'text-success', bgColor: 'bg-success' };
  };

  const handleInputChange = (field, value) => {
    setSetupData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!setupData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (setupData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!setupData?.password) {
      newErrors.password = 'Password is required';
    } else if (setupData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!setupData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (setupData?.password !== setupData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const passwordRequirements = validatePassword(setupData?.password || '');
  const passwordStrength = getPasswordStrength(setupData?.password || '');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Create Your Password</h2>
        <p className="text-muted-foreground">
          Set a secure password to protect your account
        </p>
      </div>

      {/* Form */}
      <div className="space-y-5">
        {/* Full Name */}
        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          value={setupData?.fullName}
          onChange={(e) => handleInputChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          required
        />

        {/* Password */}
        <div className="space-y-2">
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={setupData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>

          {/* Password Strength */}
          {setupData?.password && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Password Strength:</span>
                <span className={`text-xs font-medium ${passwordStrength?.color}`}>
                  {passwordStrength?.label}
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${passwordStrength?.bgColor} transition-all duration-300`}
                  style={{ width: `${(Object.values(passwordRequirements)?.filter(Boolean)?.length / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Re-enter your password"
            value={setupData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>

        {/* Password Requirements */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">Password Requirements:</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Icon
                name={passwordRequirements?.length ? 'CheckCircle2' : 'Circle'}
                size={16}
                className={passwordRequirements?.length ? 'text-success' : 'text-muted-foreground'}
              />
              <span className="text-xs text-muted-foreground">At least 8 characters</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name={passwordRequirements?.uppercase ? 'CheckCircle2' : 'Circle'}
                size={16}
                className={passwordRequirements?.uppercase ? 'text-success' : 'text-muted-foreground'}
              />
              <span className="text-xs text-muted-foreground">One uppercase letter</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name={passwordRequirements?.lowercase ? 'CheckCircle2' : 'Circle'}
                size={16}
                className={passwordRequirements?.lowercase ? 'text-success' : 'text-muted-foreground'}
              />
              <span className="text-xs text-muted-foreground">One lowercase letter</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name={passwordRequirements?.number ? 'CheckCircle2' : 'Circle'}
                size={16}
                className={passwordRequirements?.number ? 'text-success' : 'text-muted-foreground'}
              />
              <span className="text-xs text-muted-foreground">One number</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name={passwordRequirements?.special ? 'CheckCircle2' : 'Circle'}
                size={16}
                className={passwordRequirements?.special ? 'text-success' : 'text-muted-foreground'}
              />
              <span className="text-xs text-muted-foreground">One special character</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="flex-1"
          iconName="ArrowLeft"
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="default"
          size="lg"
          className="flex-1"
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default PasswordSetupStep;